import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { PaleState } from '@/interfaces';
import { enclosingBoundingBox } from '@/lib/utils/mapData';
import { filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';

export const SmallGeoMap = () => {
    const filters = useSelector((s: PaleState) => s.filters);
    const enclosing = enclosingBoundingBox(filters.destinations.map((d) => d.boundingBox));
    const destinationLabel = filters.destinations.map((d) => d.label).join(' og ') || undefined;

    const mapCenter = useMemo(() => {
        if (enclosing) {
            return { lat: (enclosing.minLat + enclosing.maxLat) / 2, lng: (enclosing.minLon + enclosing.maxLon) / 2 };
        }
        return { lat: 40, lng: 15 };
    }, [enclosing]);

    const zoomLevel = useMemo(() => {
        if (!enclosing) return 4;
        const maxDiff = Math.max(Math.abs(enclosing.maxLat - enclosing.minLat), Math.abs(enclosing.maxLon - enclosing.minLon));

        if (maxDiff > 20) return 3;
        if (maxDiff > 10) return 4;
        if (maxDiff > 5) return 5;
        if (maxDiff > 2) return 6;
        if (maxDiff > 1) return 7;
        return 8;
    }, [enclosing]);

    const staticMapImageUrl = `https://maptiles.finncdn.no/staticmap?lat=${mapCenter.lat}&lng=${mapCenter.lng}&zoom=${zoomLevel}&maptype=norwayVector&showPin=false&size=590x194`;

    const mapUrlWithFilters = useMemo(() => {
        const queryString = stringifyParams(filtersToBrowserQueryParams(filters));
        return queryString ? `/reise/pakkereiser/kart?${queryString}` : '/reise/pakkereiser/kart';
    }, [filters]);

    return (
        <a href={mapUrlWithFilters} className="block border rounded-8 mb-16" data-testid="smallGeoMap" aria-label="Vis i kart">
            <img
                src={staticMapImageUrl}
                alt={destinationLabel ? `Kart over ${destinationLabel}` : 'Vis på kart'}
                className="w-full rounded-t-8 cursor-pointer hover:opacity-90 transition-opacity"
            />
            <p className="text-center py-12 m-0">Vis i kart</p>
        </a>
    );
};
